import React, { Children, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { colors } from '../../utils/config';

/** Button background colors/gradients per provider; add or adjust provider colors here. */
const providerBackgrounds = {
	regiorad: 'background-color: #06507f',
	stella: 'background-color: #e32e20',
	voi: 'background-color: #f46c63',
	lime: 'background-color: #23d000',
	bolt: 'background-color: #32bb78',
	dott: 'background-color: #009ddb',
	zeus:
		'background: linear-gradient(135deg, rgb(251, 123, 30) 0%, rgb(251, 123, 30) 26%, rgb(250, 96, 29) 64%, rgb(247, 24, 24) 100%)',
	'ökostadt-renningen':
		'background: linear-gradient(180deg, rgb(252, 193, 7) 0%, rgb(232, 88, 13) 100%)',
	stadtmobil: 'background-color: #e2000f',
	car2go: 'background-color: #1498e3',
	flinkster: 'background-color: #e2000f',
};

/** Generated styled-components CSS rules applying providerBackgrounds to the two marker selectors per provider. */
const providerColorRules = css`
	${Object.entries(providerBackgrounds).map(
		([provider, background]) => `
	.marker--sharing-${provider} &,
	.marker__tooltip--${provider} & {
		${background};
	}`,
	)}
`;

const StyledButton = styled.button`
	border: 0;
	border-radius: 3px;
	display: block;
	font-size: 15px;
	line-height: 19px;
	margin-bottom: 16px;
	padding: 8px 40px;
	position: relative;
	text-align: center;
	width: 100%;
	z-index: 0;

	> span {
		color: ${colors.white};
	}

	&:hover {
		cursor: pointer;
	}

	&::before {
		content: '';
		display: block;
		position: absolute;
		top: 6px;
		left: 12px;
		width: 18px;
		height: 18px;
		background-image: url("data:image/svg+xml,%3Csvg width='18' height='18' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke-width='2' stroke='%23fff' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8.467 17H17V1H1v8.533'/%3E%3Cpath d='M10 13.25V8H4.75M10 8l-7 7'/%3E%3C/g%3E%3C/svg%3E");
		background-repeat: no-repeat;
		background-size: 70%;
		background-position: center bottom;
	}

	${providerColorRules}
`;

const touchstart = (e) => e.target.setAttribute('data-touchstart', Date.now());

const SharingButton = ({
	iosDeeplink,
	iosStore,
	androidDeeplink,
	androidStore,
	windowsDeeplink,
	windowsStore,
	uri,
	openPopup,
	children,
}) => {
	const buttonRef = createRef();

	const tryApp = (deeplink, store, storeName) => {
		let timeout;

		if (!deeplink) {
			document.location = uri;
			return;
		}

		function preventPopup() {
			clearTimeout(timeout);
			window.removeEventListener('blur', preventPopup);
			window.removeEventListener('pagehide', preventPopup);
			document.removeEventListener('visibilitychange', preventPopup);
		}

		window.addEventListener('blur', preventPopup);
		window.addEventListener('pagehide', preventPopup);
		document.addEventListener('visibilitychange', preventPopup);

		timeout = setTimeout(() => {
			preventPopup();
			if (store) {
				// Deeplink not working, open store?
				openPopup('Die App dieses Anbieters scheint nicht installiert zu sein.', [
					{
						label: `App aus dem ${storeName} herunterladen`,
						uri: store,
					},
					{
						label: 'Zur Webseite des Anbieters gehen',
						uri,
					},
				]);
			} else {
				document.location = uri;
			}
		}, 400);

		document.location = deeplink;
	};

	const handleClick = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const ua = window.navigator.userAgent;

		if (/Android/.test(ua)) {
			tryApp(androidDeeplink, androidStore, 'Play Store');
		} else if (/(iPhone|iPad|iPod)/.test(ua)) {
			tryApp(iosDeeplink, iosStore, 'App Store');
		} else if (/(Windows Phone|iemobile|WPDesktop)/i.test(ua)) {
			tryApp(windowsDeeplink, windowsStore, 'Microsoft Store');
		} else {
			document.location = uri;
		}
	};

	const touchend = (e) => {
		if (parseInt(e.target.getAttribute('data-touchstart'), 10) + 200 > Date.now()) {
			handleClick(e);
		}
	};

	useEffect(() => {
		const button = buttonRef.current;
		button.addEventListener('click', handleClick, false);
		button.addEventListener('touchstart', touchstart, false);
		button.addEventListener('touchend', touchend, false);
		return () => {
			button.removeEventListener('click', handleClick, false);
			button.removeEventListener('touchstart', touchstart, false);
			button.removeEventListener('touchend', touchend, false);
		};
	}, []);

	return (
		<StyledButton type="button" ref={buttonRef}>
			<span>{Children.toArray(children)}</span>
		</StyledButton>
	);
};

SharingButton.propTypes = {
	iosDeeplink: PropTypes.any,
	iosStore: PropTypes.any,
	androidDeeplink: PropTypes.any,
	androidStore: PropTypes.any,
	windowsDeeplink: PropTypes.any,
	windowsStore: PropTypes.any,
	uri: PropTypes.string.isRequired,
	openPopup: PropTypes.func,
	children: PropTypes.node.isRequired,
};

SharingButton.defaultProps = {
	openPopup: () => {},
};

export default SharingButton;
